import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import type { PropertyGroupResponse, SplitPropertyGroupRequest } from '@/shared/api';
import { NewObjectFields, draftIsComplete, emptyDraft } from './NewObjectFields';

interface Props {
  source: PropertyGroupResponse;
  pending?: boolean;
  onClose: () => void;
  onConfirm: (body: SplitPropertyGroupRequest) => void;
}

/**
 * Apartments aus einem Objekt herauslösen, weil sie in Wahrheit woanders stehen.
 *
 * Währung und Zeitzone erbt das neue Objekt vom Quellobjekt — der Server verlangt Gleichheit, und
 * eine Auswahl anzubieten hieße, eine Ablehnung anzubieten.
 */
export function SplitObjectDialog({ source, pending, onClose, onConfirm }: Props) {
  const [selected, setSelected] = useState<string[]>([]);
  const [draft, setDraft] = useState(emptyDraft);

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );

  const ready = selected.length > 0 && draftIsComplete(draft);

  return (
    <Dialog open onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>„{source.name}" teilen</DialogTitle>
          <DialogDescription>
            Die gewählten Apartments ziehen in ein neues Objekt mit eigener Adresse um. Alles andere
            bleibt, wo es ist.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-5">
          <fieldset className="space-y-2">
            <legend className="text-ink-soft mb-1 text-xs uppercase tracking-wider">
              Umziehende Apartments
            </legend>
            {source.apartments.map((apartment) => (
              <label
                key={apartment.id}
                className="border-line bg-card-alt/40 flex cursor-pointer items-center gap-3 rounded-md border p-2.5 text-sm"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(apartment.id)}
                  disabled={pending}
                  onChange={() => toggle(apartment.id)}
                />
                <span className="text-ink">{apartment.name}</span>
              </label>
            ))}
          </fieldset>

          <NewObjectFields draft={draft} disabled={pending} onChange={setDraft} />

          <p className="text-ink-soft text-xs">
            Das neue Objekt übernimmt Währung ({source.currency}) und Zeitzone ({source.timezone})
            von „{source.name}". Die Adresse muss sich von der bisherigen unterscheiden.
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button
            variant="brand"
            loading={pending}
            disabled={pending || !ready}
            onClick={() =>
              onConfirm({
                propertyIds: selected,
                newGroup: {
                  name: draft.name.trim(),
                  address: draft.address,
                  needsAddressReview: draft.needsAddressReview,
                  currency: source.currency,
                  timezone: source.timezone,
                },
              })
            }
          >
            Objekt teilen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
